import { RestClient } from '@/api/rest';
import type { SessionBlocker } from '@/api/rest';
import { config } from '@/config';
import { useGame } from '@/store/game';
import { storage } from '@/store/storage';
import { getInitDataRaw, getStartParam, initTelegram } from '@/telegram';
import type { ConnectOpts, GameTransport } from '@/transport/types';
import { WsTransport } from '@/transport/ws';

import { resolveRoomCode } from './room-code';
import { watchVisibility } from './visibility';

export interface AppRuntime {
  /**
   * `null` не значит «сломано»: экран обязан пережить отсутствие REST и
   * показать, что создание комнаты недоступно. В VITE_DEV_OFFLINE клиент есть,
   * но ходит в подменённый fetch (src/dev/offline-rest.ts).
   */
  restClient: RestClient | null;
  transport: GameTransport;
  /** Подключиться к комнате — например, после того как экран её создал или ввёл код руками. */
  connectToRoom(roomCode: string): Promise<void>;
  /** Снять подписку на уход в фон. Нужно тестам и полному демонтажу приложения. */
  stopWatchingVisibility(): void;
}

interface Wired {
  transport: GameTransport;
  getToken: ConnectOpts['getToken'];
  restClient: RestClient | null;
}

/** Тупик приезжает из REST, показывает его App: связывает их одна эта строка. */
const onBlocked = (reason: SessionBlocker): void => useGame.getState().block(reason);

/**
 * Подписанная initData для живого дев-режима в обычном браузере.
 *
 * Сервер проверяет подпись всегда, и самодельный мок он отвергает — без этой
 * строки вне Telegram нельзя ни войти, ни собрать комнату. Подписывает
 * dev-сервер: токен бота живёт в его процессе и в бандл не попадает
 * (scripts/dev-initdata.mjs).
 *
 * Внутри настоящего Telegram строка не нужна и не используется: `initTelegram`
 * не подменяет окружение, когда оно настоящее. Отказ ручки (нет токена в .env)
 * не фатален — приложение поднимется, а вход честно не пройдёт.
 */
async function devLiveInitData(): Promise<{ initDataRaw?: string }> {
  // Литералом на месте, как и офлайн-гейт: так esbuild выкидывает ветку из
  // прод-бандла целиком, а не просто не выполняет её.
  if (!(import.meta.env.DEV && import.meta.env.VITE_DEV_LIVE === '1')) return {};

  const startapp = new URLSearchParams(globalThis.location?.search ?? '').get('startapp');
  try {
    const response = await fetch(`/__dev/init-data${startapp ? `?startapp=${startapp}` : ''}`);
    if (!response.ok) return {};
    const body = (await response.json()) as { initDataRaw?: string };
    return body.initDataRaw ? { initDataRaw: body.initDataRaw } : {};
  } catch {
    return {};
  }
}

async function wireTransport(): Promise<Wired> {
  // Гейт написан именно как `import.meta.env.DEV && ...`, литералом на месте
  // вызова: так esbuild сворачивает ветку в `if (false)` при прод-сборке и
  // выкидывает динамический import() целиком, а не просто не вызывает его —
  // см. отчёт дев-платформы и src/dev/*.
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_OFFLINE === '1') {
    const [{ createOfflineTransport }, { getOfflineToken }, { createOfflineRestClient }] =
      await Promise.all([
        import('@/dev/offline-transport'),
        import('@/dev/offline-session'),
        import('@/dev/offline-rest'),
      ]);
    // RestClient здесь не `null`: без него не рисуется витрина паков и лобби
    // упирается в «недоступно офлайн». Подменён у него только fetch
    // (src/dev/offline-rest.ts).
    return {
      transport: createOfflineTransport(),
      getToken: getOfflineToken,
      restClient: createOfflineRestClient(onBlocked),
    };
  }

  const restClient = new RestClient({
    baseUrl: config.apiOrigin,
    getInitData: getInitDataRaw,
    onBlocked,
  });
  return {
    transport: new WsTransport(),
    getToken: (opts) => restClient.getToken(opts),
    restClient,
  };
}

/**
 * Собирает платформенный слой: Telegram, транспорт, сессию, стор. Экраны
 * (src/screens/) этим модулем не пользуются напрямую — они читают `useGame`.
 *
 * Подключение к комнате случается только если код уже известен (startapp или
 * localStorage): создание новой комнаты — действие экрана, а не бутстрапа.
 */
export async function bootstrap(): Promise<AppRuntime> {
  initTelegram(await devLiveInitData());

  const startParam = getStartParam(); // холодный старт: ровно один раз, здесь
  const roomCode = resolveRoomCode(startParam, storage.roomCode());

  const { transport, getToken, restClient } = await wireTransport();

  useGame.getState().attach(transport);

  const connectToRoom = (code: string): Promise<void> =>
    transport.connect({ url: config.wsUrl, roomCode: code, getToken });

  if (roomCode) {
    // Обрыв на самом старте (мёртвая initData, недоступный сервер) уже виден
    // через статус в сторе — здесь достаточно не уронить необработанным rejection.
    connectToRoom(roomCode).catch(() => undefined);
  }

  const stopWatchingVisibility = watchVisibility();

  return { restClient, transport, connectToRoom, stopWatchingVisibility };
}
